import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createOaCreationJob } from 'api'
import { queryKeys } from 'constants/queryKeys'
import { oaCreationStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import type { TCreateOaCreationJobRequest, TOaCreationJob } from 'types'
import { toErrorMessage } from './utils'

type UseCreateOaCreationJobOptions = {
  onSuccess?: (job: TOaCreationJob) => void
}

type UseCreateOaCreationJobResult = {
  create: (request: TCreateOaCreationJobRequest) => void
  isSubmitting: boolean
  error: string | undefined
}

export function useCreateOaCreationJob(
  options: UseCreateOaCreationJobOptions = {},
): UseCreateOaCreationJobResult {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | undefined>()

  const mutation = useMutation({
    mutationFn: async (request: TCreateOaCreationJobRequest) => {
      if (!user) {
        throw new Error(oaCreationStrings.errors.unauthorized)
      }
      return createOaCreationJob(request)
    },
    onSuccess: (job) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OA_CREATION_JOBS, user?.uid],
      })
      setError(undefined)
      options.onSuccess?.(job)
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, oaCreationStrings.errors.submitFailed))
    },
  })

  const create = (request: TCreateOaCreationJobRequest) => {
    setError(undefined)
    mutation.mutate(request)
  }

  return {
    create,
    isSubmitting: mutation.isPending,
    error,
  }
}
